import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Divider,
  FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  MonitorHeart, Refresh, CheckCircle, ErrorOutline, WarningAmber,
  ExpandMore, RemoveCircleOutline,
} from '@mui/icons-material';
import apiService from '../services/api';
import MachineCard from '../components/MachineCard';

const STATUS: Record<string, { color: string; label: string; icon: any }> = {
  pass: { color: '#22C55E', label: 'PASS', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
  warn: { color: '#F59E0B', label: 'WARN', icon: <WarningAmber sx={{ fontSize: 16 }} /> },
  fail: { color: '#EF4444', label: 'FAIL', icon: <ErrorOutline sx={{ fontSize: 16 }} /> },
  skipped: { color: '#6B7280', label: 'SKIPPED', icon: <RemoveCircleOutline sx={{ fontSize: 16 }} /> },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS[status] || STATUS.skipped;
  return (
    <Chip size="small" icon={s.icon} label={s.label}
      sx={{
        height: 22, fontSize: '0.62rem', fontWeight: 700,
        bgcolor: s.color + '22', color: s.color,
        '& .MuiChip-icon': { color: s.color },
      }} />
  );
}

export default function Health() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [includeSlow, setIncludeSlow] = useState(false);

  const load = useCallback(async (slow: boolean) => {
    setLoading(true); setErr(null);
    try {
      setData(await apiService.getAppHealth(slow));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Health check failed to run.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(includeSlow); }, [load, includeSlow]);

  const engines = data?.engines?.engines || [];
  const dataResults = data?.data?.results || [];
  const problems = data?.problems || [];
  const eSum = data?.engines?.summary || {};
  const dSum = data?.data?.summary || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <MonitorHeart sx={{ color: '#EF4444' }} />
        <Typography variant="h5" fontWeight="bold">System Health</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={includeSlow}
            onChange={e => setIncludeSlow(e.target.checked)} />}
          label={<Typography variant="caption">Include slow checks</Typography>}
        />
        <Button size="small" startIcon={<Refresh />} onClick={() => load(includeSlow)}
          disabled={loading}>Re-run</Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every engine is exercised and its output checked against what a healthy answer looks
        like — not merely whether it returned without error. Tables and engines fail
        independently: a table can be perfectly fresh while the engine reading it returns
        nothing, and an engine can return plausible output built from data that stopped
        updating a week ago.
      </Typography>

      <MachineCard />

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {data && (
        <>
          <Card sx={{ mb: 2, borderLeft: `4px solid ${data.healthy ? '#22C55E' : '#EF4444'}` }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6" fontWeight={700}
                sx={{ color: data.healthy ? '#22C55E' : '#EF4444' }}>
                {data.headline}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Engines: {eSum.pass || 0} pass · {eSum.warn || 0} warn · {eSum.fail || 0} fail
                  {eSum.skipped ? ` · ${eSum.skipped} skipped` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Data: {dSum.pass || 0} pass · {dSum.warn || 0} warn · {dSum.fail || 0} fail
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {problems.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Needs attention
                </Typography>
                {problems.map((p: any, i: number) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}>
                    <StatusChip status={p.severity} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      <strong>{p.area}</strong> — {p.text}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Engines ({engines.length})
          </Typography>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead><TableRow>
                  {['Status', 'Engine', 'Result', 'Time'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {engines.map((e: any) => (
                    <TableRow key={e.engine} hover>
                      <TableCell sx={{ width: 100 }}><StatusChip status={e.status} /></TableCell>
                      <TableCell>
                        <Tooltip title={e.why_it_matters || e.describe || ''}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{e.engine}</Typography>
                            <Typography variant="caption" color="text.disabled"
                              sx={{ fontSize: '0.65rem' }}>{e.area}</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {e.detail}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                        {e.seconds != null ? `${e.seconds}s` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Data checks ({dataResults.length})
          </Typography>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead><TableRow>
                  {['Status', 'Table', 'Check', 'Detail'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {dataResults.map((r: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ width: 100 }}><StatusChip status={r.status} /></TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{r.table}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        <Tooltip title={r.why_it_matters || ''}>
                          <span>{r.check}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {r.detail}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" fontWeight={600}>
                Why these checks exist
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Every data problem this project has hit was invisible in the place it
                originated. Absence and staleness look exactly like a quiet period, and
                duplication looks like health:
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" component="div">
                • SPY silently stopped being collected when the universe went stocks-only —
                every excess-vs-SPY figure would have frozen with nothing raising.<br />
                • The volatility scan ran seven days a week, writing 184,795 weekend rows
                against Friday's unchanged prices. The row count looked <em>healthier</em>.<br />
                • Delisted symbols kept their final bars, and a frozen series scores as a
                perfect breakout — one reached the buy list at 95.2.<br />
                • The fundamentals collector failed on all 500 symbols daily for over a week
                while logging "Collection successful".
              </Typography>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
